import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { CustomActivity } from 'app/model/pipeline/custom.activity';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureBatchLinkedService } from 'app/model/linkedservice/azure-batch.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { Dataset } from 'app/model/dataset/dataset';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { ValueAdapter } from './../value.adapter';
import _ from 'lodash';
import { convertParametersToModel, fillParameterValuesForResource } from 'app/shared/utils/parameter-converter.util';
import { ParameterValue } from 'app/model/base/parameter-value';
import { ReferenceObjectParameters } from 'app/model/pipeline/reference-object-parameters';
import { Owned } from 'app/model/base/named';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { ActivityConnectionContext, IAzureBatchActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { ModelService } from 'app/model/services/model.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { IFrameService } from 'app/services/iframe.service';
import { getFabricLsForParameterizedConnections } from '../utils';
import { ModelConstants } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(CustomActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            command: '*',
            getDebugInfo: '*',
            resourceLinkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            folderPath: '*',
            referenceObjects: {
                datasets: '*',
                linkedServices: '*'
            },
            extendedProperties: '*',
            retentionTimeInDays: '*'
        },
        staging: {
            linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer
        },
        externalReferences: '*'
    }
})
export class CustomActivityAdapter extends ActivityAsyncAdapter<
    CustomActivity,
    Model.CustomActivity,
    IAzureBatchActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(CustomActivity);
    }

    public override async toFabricCopilotContext(activity: CustomActivity): Promise<IAzureBatchActivityContext> {
        if (activity) {
            const activityContext: IAzureBatchActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.Command = ValueAdapter.toResource(activity, CustomActivity.COMMAND, true);
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    AzureBatchLinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
            }
            if (activity.resourceLinkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    AzureStorageLinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.resourceLinkedService));
                activityContext.ResourceConnection = await lsAdapter?.toFabricCopilotContext(
                    activity.resourceLinkedService
                );
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IAzureBatchActivityContext,
        model?: CustomActivity
    ): Promise<CustomActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, CustomActivity.COMMAND, context.Command);
            const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
            const iframeService = await getServiceInstance<IFrameService>(this.context.contextData, 'IFrameService');
            if (context.Connection) {
                model.linkedService = (
                    await findLinkedServiceFromContext<AzureBatchLinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                              AzureBatchLinkedService,
                              Model.LinkedServiceResource,
                              ActivityConnectionContext
                          >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    if (lsAdapter) {
                        await lsAdapter.fromFabricCopilotContext(context.Connection, model.linkedService);
                    }
                }
            }
            if (context.ResourceConnection) {
                model.resourceLinkedService = (
                    await findLinkedServiceFromContext<AzureStorageLinkedService>(context.ResourceConnection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.resourceLinkedService) {
                    const lsAdapter = Metamodel.isModel(context.ResourceConnection.Type)
                        ? this.context.asyncAdapterFor<
                              AzureStorageLinkedService,
                              Model.LinkedServiceResource,
                              ActivityConnectionContext
                          >(Metamodel.modelByName(context.ResourceConnection.Type))
                        : undefined;
                    if (lsAdapter) {
                        await lsAdapter.fromFabricCopilotContext(
                            context.ResourceConnection,
                            model.resourceLinkedService
                        );
                    }
                }
            }
        }
        return model;
    }

    protected async _toResource(activity: CustomActivity): Promise<Model.CustomActivity> {
        const modelCustomActivity: Model.CustomActivity = super._getCommonActivityProperties(
            activity,
            'Custom'
        ) as Model.CustomActivity;
        const customActivity: CustomActivity = activity as CustomActivity;

        modelCustomActivity.typeProperties = {
            command: ValueAdapter.toResource(customActivity, CustomActivity.COMMAND),
            resourceLinkedService: activity.resourceLinkedService
                ? this._toLinkedServiceReferenceModel(activity.resourceLinkedService)
                : undefined,
            folderPath: ValueAdapter.toResource(customActivity, CustomActivity.FOLDER_PATH),
            extendedProperties: toParameterObject(activity.extendedProperties),
            retentionTimeInDays: activity.retentionTimeInDays
        };

        if (!__IS_FABRIC_DPE__) {
            modelCustomActivity.typeProperties.referenceObjects = {
                linkedServices: this.getLinkedServiceReferences(activity),
                datasets: this.getDatasetsReferences(activity)
            };
        }

        if (activity.linkedService) {
            modelCustomActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService);
        }

        if (activity.stagingLinkedService) {
            modelCustomActivity.staging = {
                linkedServiceName: this._toLinkedServiceReferenceModel(activity.stagingLinkedService)
            };
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on hive activity
            _.merge(modelCustomActivity, parsed);
        }
        return modelCustomActivity;
    }

    protected async _toModel(modelActivity: Model.CustomActivity): Promise<CustomActivity> {
        const activity: CustomActivity = new CustomActivity(modelActivity.name);
        const modelCustomActivity = modelActivity as Model.CustomActivity;

        this._updatedCommonProperties(modelActivity, activity);

        if (modelCustomActivity.typeProperties) {
            ValueAdapter.toModel(activity, CustomActivity.COMMAND, modelCustomActivity.typeProperties.command);
            if (modelCustomActivity.typeProperties.resourceLinkedService) {
                const resourceLs = getFabricLsForParameterizedConnections(
                    modelCustomActivity.typeProperties.resourceLinkedService,
                    ModelConstants.blobLinkedService,
                    activity
                );
                activity.resourceLinkedService = resourceLs ? (resourceLs as AzureStorageLinkedService)
                    : ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(
                        Metamodel.modelFor(AzureStorageLinkedService),
                        modelCustomActivity.typeProperties.resourceLinkedService,
                        this.context
                    );               
            }
            ValueAdapter.toModel(activity, CustomActivity.FOLDER_PATH, modelCustomActivity.typeProperties.folderPath);
            activity.retentionTimeInDays = modelCustomActivity.typeProperties.retentionTimeInDays;
            activity.extendedProperties = toParameterClass(
                modelCustomActivity.typeProperties.extendedProperties,
                activity
            );
            // referenceObjects
            activity.referenceObjects.length = 0;
            if (modelCustomActivity.typeProperties.referenceObjects) {
                if (modelCustomActivity.typeProperties.referenceObjects.linkedServices) {
                    modelCustomActivity.typeProperties.referenceObjects.linkedServices.forEach((linkedService) => {
                        const refParam = Owned.create<ReferenceObjectParameters>(
                            ReferenceObjectParameters.MODEL,
                            activity
                        );
                        const refLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                            Metamodel.modelFor(LinkedService),
                            linkedService,
                            this.context
                        );
                        const paramValues: ParameterValue[] = [];

                        if (linkedService.parameters) {
                            convertParametersToModel(
                                refParam,
                                !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                                refLinkedService,
                                paramValues,
                                linkedService.parameters
                            );

                            refParam.parameters = paramValues;
                        }

                        activity.referenceObjectsParameters.push(refParam);
                        activity.referenceObjects.push(refLinkedService);
                    });
                }
                if (modelCustomActivity.typeProperties.referenceObjects.datasets) {
                    modelCustomActivity.typeProperties.referenceObjects.datasets.forEach((dataset) => {
                        const refDataset = ReferenceAdapter.toProxyModel<Dataset>(
                            Metamodel.modelFor(Dataset),
                            dataset,
                            this.context
                        );
                        const paramValues: ParameterValue[] = [];
                        if (dataset.parameters) {
                            const refParam = Owned.create<ReferenceObjectParameters>(
                                ReferenceObjectParameters.MODEL,
                                activity
                            );

                            convertParametersToModel(
                                refParam,
                                !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                                refDataset,
                                paramValues,
                                dataset.parameters
                            );

                            refParam.parameters = paramValues;
                            activity.referenceObjectsParameters.push(refParam);
                        } else {
                            activity.referenceObjectsParameters.push(
                                Owned.create<ReferenceObjectParameters>(ReferenceObjectParameters.MODEL, activity)
                            );
                        }
                        activity.referenceObjects.push(refDataset);
                    });
                }
            }
        }

        if (modelCustomActivity.linkedServiceName !== undefined) {
            const ls = getFabricLsForParameterizedConnections(
                modelCustomActivity.linkedServiceName,
                ModelConstants.azureBatchLinkedService,
                activity
            );
            activity.linkedService = ls ? (ls as AzureBatchLinkedService) :
                ReferenceAdapter.toProxyModel<AzureBatchLinkedService>(
                    Metamodel.modelFor(AzureBatchLinkedService),
                    modelCustomActivity.linkedServiceName,
                    this.context
                );
        }

        if (modelCustomActivity.staging?.linkedServiceName !== undefined) {
            activity.stagingLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(
                Metamodel.modelFor(AzureStorageLinkedService),
                modelCustomActivity.staging.linkedServiceName,
                this.context
            );
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelCustomActivity, CustomActivity.MODEL)
        );

        return activity;
    }

    private getDatasetsReferences(activity: CustomActivity): Model.DatasetReference[] {
        const modelDatasetRefs: Model.DatasetReference[] = [];
        activity.referenceObjects.forEach((refObj, idx) => {
            if (refObj instanceof Dataset) {
                const datasetReference = ReferenceAdapter.toResource(refObj, this.context) as Model.DatasetReference;
                const datasetParameters: ParameterValue[] = [];
                activity.referenceObjectsParameters[idx].parameters?.forEach((param) => datasetParameters.push(param));
                datasetReference.parameters = fillParameterValuesForResource(
                    datasetParameters,
                    this.context,
                    this.ensureLogger()
                );
                modelDatasetRefs.push(datasetReference);
            }
        });
        return modelDatasetRefs;
    }

    private getLinkedServiceReferences(activity: CustomActivity): Model.DatasetReference[] {
        const modelLinkedServiceRefs: Model.LinkedServiceReference[] = [];
        activity.referenceObjects.forEach((refObj, idx) => {
            if (refObj instanceof LinkedService) {
                const linkedServiceReference = ReferenceAdapter.toResource(
                    refObj,
                    this.context
                ) as Model.LinkedServiceReference;
                const linkedServiceParameters: ParameterValue[] = [];
                activity.referenceObjectsParameters[idx].parameters?.forEach((param) =>
                    linkedServiceParameters.push(param)
                );
                linkedServiceReference.parameters = fillParameterValuesForResource(
                    linkedServiceParameters,
                    this.context,
                    this.ensureLogger()
                );
                modelLinkedServiceRefs.push(linkedServiceReference);
            }
        });
        return modelLinkedServiceRefs;
    }
}
